import * as Sentry from '@sentry/react';
import React from 'react';
import {StringParam, useQueryParam} from 'use-query-params';

import ErrorPage from 'app/components/Error/ErrorPage';
import Loading from 'app/components/Loading';
import PublicReport, {ReportSharePayload} from 'app/components/PublicReport';
import {api} from 'app/modules/Remote';
import {ApiPublicLensResponse} from 'app/modules/Remote/api';

type PublicErrorTypes = 'unavailable' | 'transient' | null;

/**
 * The page served at /p/r/:uuid. Loads the share link's payload — the same `/p/<uuid>`
 * response a map link renders from, plus the report's metadata — and hands it to the
 * report renderer. A link without a report, or one made for the story page, reads as
 * unavailable: a link opens only as the presentation it was made for.
 */
const PublicReportPage: React.FunctionComponent<{uuid: string}> = ({uuid}) => {
  const [payload, setPayload] = React.useState<ApiPublicLensResponse | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [errorState, setErrorState] = React.useState<PublicErrorTypes>(null);

  const isEmbedded = useQueryParam('embedded', StringParam)[0] === 'true';

  React.useEffect(() => {
    const fetchShareLink = async () => {
      try {
        setIsLoading(true);
        const response = await api.public(uuid);
        setPayload(response.get('data').toJS() as ApiPublicLensResponse);
      } catch (e) {
        /*
          Some extra error handling, since this is a public page.
          The API tends to return 4XX series errors to communicate that no resource exists for the request,
          which in this case means a share link is no longer available or 'no longer linked'.
          We'll get 500 series errors if there's an issue with the server, which is either a bug or a transient error.
        */
        const {error, status} = e as {error: Error; status: number};
        if (400 <= status && status <= 499) {
          setErrorState('unavailable');
        } else if (status >= 500) {
          setErrorState('transient');
          Sentry.captureException(error);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchShareLink();
  }, [uuid]);

  if (isLoading) {
    return <Loading />;
  }

  if (errorState || !isReportPayload(payload)) {
    return (
      <>
        <ErrorPage
          message={
            errorState === 'transient'
              ? 'Something went wrong. Please wait a moment, then reload the page.'
              : 'This content is no longer available.'
          }
          linkTo={isEmbedded ? null : 'https://www.upstream.tech/lens'}
        />
        {/* The PDF export job waits on this rather than timing out on a dead link. */}
        <div id="report-error" data-testid="report-error" aria-hidden />
      </>
    );
  }

  return <PublicReport uuid={uuid} payload={payload} />;
};

const isReportPayload = (payload: ApiPublicLensResponse | null): payload is ReportSharePayload =>
  payload?.report?.presentation === 'document';

export default PublicReportPage;
